import {
    Grid,
    Paper,
    Typography,
    TextField,
    Box,
    Autocomplete,
    Tooltip,
    IconButton,
    Stack
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { usoCFDI, taxRegimen, tipoComprobante } from "../constants/catalogs";
import { useClientValidations } from "../hooks/useClientValidations";
import type { Option, SerieItem, ClienteProps } from "../types/invoice";

const sxInputHeight = {
    "& .MuiInputBase-root": { height: 40 }
};

const Client = ({
    cliente,
    onSelectCliente,
    clientesList,

    tipo,
    setTipo,

    seriesOptions,
    serie,
    setSerieByValue,
    folio,
    onRefreshSeries,

    rfc,
    usoCFDIValue,
    setUsoCFDIValue,

    regimenFiscal,
    setRegimenFiscal,

    onRefreshClients,
    onEditCliente,
    onOpenQRMenu,
    onCreateCliente,

    loadingClients = false,
    errorClients = null,
    loadingSeries = false,
    errorSeries = null,
    notify
}: ClienteProps) => {
    // Lógica de validaciones movida al hook:
    useClientValidations({ cliente, regimenFiscal, usoCFDIValue, notify });
    // 1) Helper para limitar coincidencias
    const limitFilter =
        (limit = 20) =>
            (options: any[], state: { inputValue: string }) => {
                const q = state.inputValue.trim().toLowerCase();
                if (!q) return options.slice(0, limit);

                // Match por business_partner y/o rfc, y corta al límite
                const out: any[] = [];
                for (let i = 0; i < options.length && out.length < limit; i++) {
                    const o = options[i];
                    const label = `${o?.business_partner ?? ""} ${o?.rfc ?? ""}`.toLowerCase();
                    if (label.includes(q)) out.push(o);
                }
                return out;
            };


    return (
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Información General
                </Typography>
            </Box>

            <Grid container spacing={2}>
                {/* Tipo de comprobante + Reconsultar series */}
                <Grid item xs={12} md={4}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ flex: 1 }}>
                            <Autocomplete<Option, false, false, false>
                                options={tipoComprobante}
                                value={tipoComprobante.find((opt) => opt.value === tipo) || null}
                                onChange={(_, value) => setTipo(value?.value || "I")}
                                getOptionLabel={(o) => o.label}
                                isOptionEqualToValue={(o, v) => o.value === v.value}
                                disablePortal
                                fullWidth
                                renderInput={(params) => (
                                    <TextField {...params} label="Tipo de Comprobante" size="small" sx={sxInputHeight} />
                                )}
                            />
                        </Box>

                        <Tooltip title="Reconsultar series/folios">
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={onRefreshSeries}
                                    sx={{ borderRadius: 2, width: 48, height: 40 }}
                                    disabled={loadingSeries}
                                    aria-label="Reconsultar series y folios"
                                >
                                    <CachedIcon
                                        sx={{
                                            ...(loadingSeries ? { animation: "spin 0.9s linear infinite" } : {}),
                                            transformOrigin: "50% 50%",
                                            "@keyframes spin": {
                                                from: { transform: "rotate(0deg)" },
                                                to: { transform: "rotate(360deg)" }
                                            }
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Grid>

                {/* Serie */}
                <Grid item xs={12} md={4}>
                    <Autocomplete<SerieItem, false, false, false>
                        options={seriesOptions}
                        value={seriesOptions.find((s) => s.serie === serie) || null}
                        onChange={(_, value) => setSerieByValue(value?.serie || "")}
                        getOptionLabel={(option) => option.serie}
                        isOptionEqualToValue={(o, v) => o.serie === v.serie}
                        disablePortal
                        fullWidth
                        loading={loadingSeries}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={loadingSeries ? "Cargando series..." : "Tipo de Serie"}
                                size="small"
                                sx={sxInputHeight}
                                error={!!errorSeries}
                                helperText={errorSeries ?? ""}
                            />
                        )}
                    />
                </Grid>

                {/* Folio */}
                <Grid item xs={12} md={4}>
                    <TextField label="Folio" value={folio} size="small" fullWidth disabled sx={sxInputHeight} />
                </Grid>

                {/* Cliente + Crear + Editar + QR + Reconsultar */}
                <Grid item xs={12}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ flex: 1 }}>
                            <Autocomplete
                                options={clientesList}
                                value={cliente || null}
                                onChange={(_, value) => onSelectCliente(value)}
                                getOptionLabel={(o) => (o?.business_partner ? `${o.business_partner} — ${o.rfc}` : "")}
                                isOptionEqualToValue={(o, v) => String(o?.id) === String(v?.id)}
                                disablePortal
                                fullWidth
                                loading={!!loadingClients}
                                filterOptions={limitFilter(20)}
                                renderOption={(props, option) => (
                                    <li {...props} key={String(option.id)}>
                                        {option.business_partner} — {option.rfc}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={loadingClients ? "Cargando clientes..." : "Buscar socio de negocios..."}
                                        size="small"
                                        sx={sxInputHeight}
                                        error={!!errorClients}
                                        helperText={errorClients ?? ""}
                                    />
                                )}
                            />
                        </Box>

                        <Tooltip title="Crear nuevo socio">
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={onCreateCliente}
                                    sx={{ borderRadius: 2, width: 48, height: 40 }}
                                    aria-label="Crear socio de negocios"
                                >
                                    <AddCircleOutlineIcon />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Editar socio seleccionado">
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={() => onEditCliente?.(cliente?.id)}
                                    sx={{ borderRadius: 2, width: 48, height: 40 }}
                                    disabled={!cliente}
                                    aria-label="Editar socio seleccionado"
                                >
                                    <EditOutlinedIcon />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Crear/Actualizar por QR">
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={(e) => onOpenQRMenu?.(e.currentTarget)}
                                    sx={{ borderRadius: 2, width: 48, height: 40 }}
                                    aria-label="Abrir opciones de QR"
                                >
                                    <QrCode2OutlinedIcon />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Reconsultar catálogo">
                            <span>
                                <IconButton
                                    color="primary"
                                    onClick={() => onRefreshClients(cliente?.id)}
                                    sx={{ borderRadius: 2, width: 48, height: 40 }}
                                    disabled={loadingClients}
                                    aria-label="Reconsultar catálogo de clientes"
                                >
                                    <CachedIcon
                                        sx={{
                                            ...(loadingClients ? { animation: "spin 0.9s linear infinite" } : {}),
                                            transformOrigin: "50% 50%",
                                            "@keyframes spin": {
                                                from: { transform: "rotate(0deg)" },
                                                to: { transform: "rotate(360deg)" }
                                            }
                                        }}
                                    />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Grid>

                {/* RFC */}
                <Grid item xs={12} md={4}>
                    <TextField label="RFC" value={rfc} size="small" fullWidth disabled sx={sxInputHeight} />
                </Grid>

                {/* Uso CFDI */}
                <Grid item xs={12} md={4}>
                    <Autocomplete<Option, false, false, false>
                        options={usoCFDI}
                        value={usoCFDI.find((opt) => opt.value === usoCFDIValue) || null}
                        onChange={(_, value) => setUsoCFDIValue(value?.value || "")}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        disablePortal
                        fullWidth
                        renderInput={(params) => (
                            <TextField {...params} label="Uso de CFDI" size="small" sx={sxInputHeight} />
                        )}
                    />
                </Grid>

                {/* Régimen Fiscal */}
                <Grid item xs={12} md={4}>
                    <Autocomplete<Option, false, false, false>
                        options={taxRegimen}
                        value={taxRegimen.find((opt) => opt.value === regimenFiscal) || null}
                        onChange={(_, value) => setRegimenFiscal(value?.value || "")}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(o, v) => o.value === v.value}
                        disablePortal
                        fullWidth
                        renderInput={(params) => (
                            <TextField {...params} label="Régimen Fiscal" size="small" sx={sxInputHeight} />
                        )}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};

export default Client;
